import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { IVideoAPIUtilities } from 'tag/ts/tag/interfaces/api/IVideoAPIUtilities';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IParamsContent } from 'tag/ts/tag/interfaces/params/IParamsContent';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Dedicated Video API utilities class
 */
export class VideoAPIUtilities extends AbstractTagClass implements IVideoAPIUtilities {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'VideoAPIUtilities'
			}
		);
	}

	/**
	 * @description Determines and returns a flag allowing video load
	 * @returns {boolean}
	 */
	allowedToLoadVideo = (): boolean => {
		// initialize our output as always allowed
		let allowed = true;
		// filter per embed type
		switch ( this.tag?.params?.SYSTEM?.embedType ) {
			case EmbedType.DESKTOP:
				this?.logger?.log( 'load:', 'casting:', this?.tag?.snapshot?.casting );
				// if we are casting on desktop => disallow video load
				allowed = !this?.tag?.snapshot?.casting;
				break;
		}
		// return the result...
		return allowed;
	};

	/**
	 * @description Clears content data from existing params
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	clearContentProperties = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve ) => {
				// reset content
				if ( this.utils.Type.isObject( this.tag ) && this.utils.Type.isObject( this.tag?.params?.CONTENT ) ) {
					// reset content properties
					[
						'apiMetadata',
						'assetURL',
						'contentId',
						'drm',
						'globalResumeTime',
						'pid'
					].forEach( ( property ) => {
						this.tag.params.CONTENT[ property ] = undefined;
					} );
					// reset context
					if (
						this.utils.Type.isObject( this.tag )
						&&
						this.utils.Type.isObject( this.tag?.params?.CONTEXT )
					) {
						this.params.CONTEXT.metadata = undefined;
					}
					this?.logger?.log( 'clearContentProperties:', 'Resolve' );
					// resolve
					resolve( { msg: `${this.name}: clearContentProperties: done!` } );
				}
			}
		);
	};

	/**
	 * @description Resets currently active player
	 * @param {IParamsVO} incomingParamsVO
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	stopPlayer = ( incomingParamsVO: IParamsVO = {} as IParamsVO ): Promise<IPromiseResolveGeneric> => {
		const loggerPrefix = 'load:';
		const loggerMethodName = 'stopPlayer:';

		return new Promise(
			( resolve ) => {

				/**
				 * @description Callback we are sending with request to stop the player.
				 * This call back will be executed in PlayerManager after player stop
				 * @param {boolean} success
				 * @param result
				 */
				const playerStopRequestCallback = ( success: boolean, result: any ): void => {
					this?.logger?.log(
						loggerPrefix,
						loggerMethodName,
						'playerStopRequestCallback Success:',
						success,
						result
					);
					switch ( success ) {
						case true:
							this?.logger?.log( loggerPrefix, loggerMethodName, 'playerStopRequestCallback', 'dispatching', TagEvent.ON_API_LOAD_STOP_PLAYER_SUCCESS, 'with params', incomingParamsVO );
							// inform application
							this.tag.dispatchTagEvent(
								this.tag.getTagEventVO(
									TagEvent.ON_API_LOAD_STOP_PLAYER_SUCCESS,
									this.tag,
									{
										paramsVO: incomingParamsVO
									},
									this,
									this.name
								)
							);
							this.utils.Delay.delay().then(
								() => {
									resolve( { msg: `${this.name}: stopPlayer: done!` } );
								}
							);
							break;
						case false:
							this?.logger?.warn( 'load: player.stop() Error:', result );
							break;
					}
				};

				// Check if player already exists (we are attempting to load video asset into a working non-failed player)
				if ( !this.utils.Type.isEmpty( this.tag?.snapshot?.player ) ) {
					this?.logger?.log( loggerPrefix, loggerMethodName, 'player is defined', 'load video during or after successful session' );
					// clear content properties on current paramsVO
					this.clearContentProperties().then(
						() => {
							this.utils.Delay.delay().then(
								() => {
									this?.logger?.log( loggerPrefix, loggerMethodName, 'dispatching', TagEvent.ON_API_LOAD_PLAYER_STOP_REQUEST );
									// dispatch application event: before player stop request
									this.tag.dispatchTagEvent(
										this.tag.getTagEventVO(
											TagEvent.ON_API_LOAD_PLAYER_STOP_REQUEST,
											this.tag,
											undefined,
											this,
											this.name
										)
									);
								}
							).then(
								() => {
									// delay actual requests to stop player by X milliseconds
									// to allow processing by tag, skin etc.
									this.utils.Delay.delay().then(
										() => {
											this?.logger?.log( loggerPrefix, 'dispatching', TagEvent.ON_TAG_PLAYER_STOP_REQUEST );
											// request player stop via event
											this.tag.dispatchTagEvent(
												// event data VO
												this.tag.getTagEventVO(
													TagEvent.ON_TAG_PLAYER_STOP_REQUEST,
													this.tag,
													{
														eventCallback: playerStopRequestCallback
													},
													this,
													this.name
												)
											);
										}
									);
								}
							);
						}
					);
				} else {
					// player is undefined
					// check if we are going to load the video after the fatal error on previous load attempt
					switch ( this?.tag?.errorManager?.fatalError ) {
						// fatal error in progress (was not cleared after failed load attempt - fatal error)
						// so, it looks like we are loading next video asset
						case true:
							this?.logger?.log( loggerPrefix, loggerMethodName, 'player is undefined', 'load video after fatal error.' );
							this.clearContentProperties().then(
								() => {
									resolve( { msg: `${this.name}: stopPlayer: done! player is undefined => load video after fatal error` } );
								}
							);
							break;
						// no fatal error in progress (it is our first load video attempt)
						case false:
							this?.logger?.log( loggerPrefix, loggerMethodName, 'player is undefined', 'first video load.' );
							resolve( { msg: `${this.name}: stopPlayer: done! player is undefined => first video load` } );
							break;
					}
				}
			}
		);
	};

	/**
	 * @description Paths within create-time buckets permitted to be overridden via load().
	 *
	 * Design-time contract — which TAG/SKIN sub-paths carry per-load semantics is architectural,
	 * not runtime config. Extensions require a PR (append an entry here); no other changes needed.
	 * Dot-notation supports arbitrarily deep targets — e.g. `'playerOptions.network.timeout'`.
	 */
	static readonly #LOAD_PARAM_WHITELIST: Array<{ bucket: string; path: string }> = [
		{ bucket: 'TAG', path: 'playerOptions' }
	];

	/**
	 * @description Extracts whitelisted dot-notation paths from create-time buckets in the
	 * incoming `load()` params and deep-merges them into live `tag.params` before those buckets
	 * are deleted. Only whitelisted paths are touched; all sibling properties are preserved.
	 *
	 * @param {IParamsVO} incomingParamsVO - Incoming params from load(), before bucket deletion
	 */
	mergeWhitelistedLoadParams = ( incomingParamsVO: IParamsVO ): void => {
		const incoming = incomingParamsVO as unknown as Record<string, unknown>;
		const tagParams = this.tag.params as unknown as Record<string, unknown>;

		for ( const { bucket, path } of VideoAPIUtilities.#LOAD_PARAM_WHITELIST ) {
			const incomingBucket = incoming[ bucket ];
			if ( !this.utils.Type.isEmpty( incomingBucket ) && this.utils.Type.isObject( incomingBucket ) ) {
				// Guard: deepmerge requires two objects — skip scalar values at the whitelisted path.
				const incomingValue = this.utils.Object.getDeepVal( incomingBucket as Record<string, unknown>, path );
				if ( !this.utils.Type.isEmpty( incomingValue ) && this.utils.Type.isObject( incomingValue ) ) {
					tagParams[ bucket ] = tagParams[ bucket ] || {};
					const existing = this.utils.Object.getDeepVal( tagParams[ bucket ] as Record<string, unknown>, path ) || {};
					const merged = this.utils.DeepMerge.deepmerge( existing, incomingValue );
					this.utils.Object.setDeepVal( tagParams[ bucket ] as Record<string, unknown>, path, merged );
					this?.logger?.log( 'mergeWhitelistedLoadParams:', `${bucket}.${path}`, 'merged into tag.params' );
				}
			}
		}
	};

	/**
	 * @description Sets loading animation background image from incoming load() params.
	 * @param {IParamsVO} paramsVO
	 */
	setLoadingAnimationBackgroundImage = ( paramsVO: IParamsVO ): void => {
		if ( !this.tag?.params?.CONTENT ) {
			this.tag.params.CONTENT = {} as IParamsContent;
		}
		this.tag.params.CONTENT = {
			...this?.tag?.params?.CONTENT,
			...paramsVO?.CONTENT
		};

		const contentParams = this?.tag?.params?.CONTENT;
		const [ apiMetadata ] = contentParams?.apiMetadata ?? [];
		const isLive = contentParams?.isLive || apiMetadata?.isLive;
		const contentBackgroundImageURL = contentParams?.loadingAnimationBackgroundImageURL;
		const currentBackgroundImageURL = this?.tag?.view?.loadingAnimationManager?.backgroundImageURL;

		if ( isLive ) {
			this?.tag?.view?.loadingAnimationManager?.disableBackgroundImage();
		} else {
			if ( this?.tag?.params?.TAG?.loadingAnimationConfig?.backgroundImage?.enabled ) {
				this?.tag?.view?.loadingAnimationManager?.enableBackgroundImage();
			}
			if ( this?.tag?.params?.TAG?.loadingAnimationConfig?.fadeIn?.enabled ) {
				this?.tag?.view?.loadingAnimationManager?.enableFadeIn();
			}
			this.#applyBackgroundImage( contentBackgroundImageURL, apiMetadata?.thumbnail, currentBackgroundImageURL );
		}
	};

	/**
	 * @description Selects and applies the best available background image URL.
	 * Prefers the content-level override; falls back to apiMetadata thumbnail.
	 * No-op when neither URL is valid or differs from the current image.
	 */
	#applyBackgroundImage = ( contentURL: string | undefined, thumbnail: string | undefined, currentURL: string | undefined ): void => {
		if ( this.utils.URL.valid( contentURL ) && contentURL !== currentURL ) {
			this?.tag?.view?.loadingAnimationManager?.setBackgroundImage( contentURL );
		} else if ( this.utils.URL.valid( thumbnail ) && thumbnail !== currentURL ) {
			this?.tag?.view?.loadingAnimationManager?.setBackgroundImage( thumbnail );
		} else {
			// no valid background image available — leave current unchanged
		}
	};

}